/**
 * Knowledge Constellation - Force-directed graph of observations
 * Shows observations as nodes connected by semantic similarity
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import * as d3 from 'd3'
import { useObservations } from '../hooks/useObservations'
import { cosineSimilarity } from '../lib/similarity'
import { AGENT_CONFIG } from '../types'
import type { Observation, GraphNode, GraphLink, GraphData } from '../types'

interface KnowledgeConstellationProps {
  width?: number
  height?: number
  similarityThreshold?: number
}

// Generate mock embeddings for testing when real ones aren't available
function generateMockEmbedding(id: string): number[] {
  const seed = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  const embedding: number[] = []
  const random = (n: number) => {
    const x = Math.sin(seed + n) * 10000
    return x - Math.floor(x)
  }
  for (let i = 0; i < 128; i++) {
    embedding.push(random(i) * 2 - 1)
  }
  return embedding
}

// Build graph data from observations
function buildGraphData(
  observations: Observation[],
  threshold: number
): GraphData {
  // Ensure all observations have embeddings
  const obsWithEmbeddings = observations.map(obs => ({
    ...obs,
    embedding: obs.embedding?.length ? obs.embedding : generateMockEmbedding(obs.id)
  }))

  // Create nodes
  const nodes: GraphNode[] = obsWithEmbeddings.map(obs => ({
    id: obs.id,
    observation: obs
  }))

  // Calculate pairwise similarities and create links
  const links: GraphLink[] = []
  for (let i = 0; i < obsWithEmbeddings.length; i++) {
    for (let j = i + 1; j < obsWithEmbeddings.length; j++) {
      const sim = cosineSimilarity(
        obsWithEmbeddings[i].embedding!,
        obsWithEmbeddings[j].embedding!
      )
      if (sim >= threshold) {
        links.push({
          source: obsWithEmbeddings[i].id,
          target: obsWithEmbeddings[j].id,
          similarity: sim
        })
      }
    }
  }

  return { nodes, links }
}

// Get node color based on agent
function getNodeColor(agent: string | undefined): string {
  if (!agent) return '#888888'
  const agentKey = agent.toLowerCase()
  return AGENT_CONFIG[agentKey]?.color || '#888888'
}

// Get node size based on importance (confidence + connection count)
function calculateNodeRadius(obs: Observation | undefined, linkCount: number): number {
  if (!obs) return 6
  const baseSize = 6
  const confidenceBoost = ((obs.confidence || 0.5) * 4)
  const connectionBoost = Math.min((linkCount || 0) * 1.5, 10)
  const radius = baseSize + confidenceBoost + connectionBoost
  return isNaN(radius) ? 6 : radius
}

// Helper to safely get node ID from link endpoint
function getNodeId(endpoint: string | GraphNode): string {
  return typeof endpoint === 'string' ? endpoint : endpoint.id
}

// Helper to safely get node from link endpoint
function getNode(endpoint: string | GraphNode): GraphNode {
  if (typeof endpoint === 'string') {
    throw new Error('Link endpoint not resolved to node')
  }
  return endpoint
}

export default function KnowledgeConstellation({
  width = 800,
  height = 600,
  similarityThreshold = 0.7
}: KnowledgeConstellationProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null)
  const [isSimulating, setIsSimulating] = useState(false)
  
  const { observations, loading, error, refetch } = useObservations({ limit: 100 })

  // Initialize D3 visualization
  const initializeGraph = useCallback(() => {
    if (!svgRef.current || observations.length === 0) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove() // Clear previous rendering

    // Build graph data
    const graphData = buildGraphData(observations, similarityThreshold)
    
    if (graphData.nodes.length === 0) return

    // Calculate link counts for sizing
    const linkCounts = new Map<string, number>()
    graphData.nodes.forEach(n => linkCounts.set(n.id, 0))
    graphData.links.forEach(l => {
      const sourceId = getNodeId(l.source)
      const targetId = getNodeId(l.target)
      linkCounts.set(sourceId, (linkCounts.get(sourceId) || 0) + 1)
      linkCounts.set(targetId, (linkCounts.get(targetId) || 0) + 1)
    })

    // Create zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform)
      })

    svg.call(zoom)

    // Create main group for zoom/pan
    const g = svg.append('g')

    // Create force simulation
    const simulation = d3.forceSimulation<GraphNode>(graphData.nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(graphData.links)
        .id(d => d.id)
        .distance(d => 100 - (d.similarity * 50)) // Closer for higher similarity
        .strength(d => d.similarity * 0.5)
      )
      .force('charge', d3.forceManyBody().strength(-150))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide<GraphNode>().radius(d => {
        const count = linkCounts.get(d.id) || 0
        return calculateNodeRadius(d.observation, count) + 5
      }))

    setIsSimulating(true)
    simulation.on('end', () => setIsSimulating(false))

    // Create links
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(graphData.links)
      .enter()
      .append('line')
      .attr('stroke', '#4b5563')
      .attr('stroke-opacity', d => 0.2 + (d.similarity - similarityThreshold) * 2)
      .attr('stroke-width', d => 0.5 + (d.similarity - similarityThreshold) * 3)

    // Create nodes group
    const node = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(graphData.nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .style('cursor', 'pointer')
      .call(d3.drag<SVGGElement, GraphNode>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart()
          d.fx = d.x
          d.fy = d.y
        })
        .on('drag', (event, d) => {
          d.fx = event.x
          d.fy = event.y
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0)
          d.fx = null
          d.fy = null
        })
      )

    // Add circles to nodes
    node.append('circle')
      .attr('r', d => {
        const count = linkCounts.get(d.id) || 0
        const radius = calculateNodeRadius(d.observation, count)
        return isNaN(radius) ? 6 : radius
      })
      .attr('fill', d => getNodeColor(d.observation?.agent))
      .attr('stroke', '#1f2937')
      .attr('stroke-width', 2)
      .style('filter', 'drop-shadow(0 0 4px rgba(0,0,0,0.5))')

    // Add glow effect for higher confidence observations
    node.append('circle')
      .attr('r', d => {
        const count = linkCounts.get(d.id) || 0
        const radius = calculateNodeRadius(d.observation, count)
        return isNaN(radius) ? 9 : radius + 3
      })
      .attr('fill', 'none')
      .attr('stroke', d => getNodeColor(d.observation?.agent))
      .attr('stroke-width', 1)
      .attr('stroke-opacity', d => (d.observation.confidence || 0.5) * 0.5)
      .style('pointer-events', 'none')

    // Add click handler
    node.on('click', (event, d) => {
      event.stopPropagation()
      setSelectedNode(d)
    })

    // Add hover effects
    node.on('mouseenter', function(_event, d) {
      d3.select(this).select('circle')
        .transition()
        .duration(200)
        .attr('r', () => {
          const count = linkCounts.get(d.id) || 0
          return calculateNodeRadius(d.observation, count) * 1.2
        })
      
      // Highlight connected links
      link.transition()
        .duration(200)
        .attr('stroke-opacity', l => {
          const sourceId = getNodeId(l.source)
          const targetId = getNodeId(l.target)
          return (sourceId === d.id || targetId === d.id) ? 0.8 : 0.1
        })
        .attr('stroke-width', l => {
          const sourceId = getNodeId(l.source)
          const targetId = getNodeId(l.target)
          return (sourceId === d.id || targetId === d.id) ? 2 : 0.5
        })
    })

    node.on('mouseleave', function(_event, d) {
      d3.select(this).select('circle')
        .transition()
        .duration(200)
        .attr('r', () => {
          const count = linkCounts.get(d.id) || 0
          return calculateNodeRadius(d.observation, count)
        })
      
      link.transition()
        .duration(200)
        .attr('stroke-opacity', d => 0.2 + (d.similarity - similarityThreshold) * 2)
        .attr('stroke-width', d => 0.5 + (d.similarity - similarityThreshold) * 3)
    })

    // Update positions on simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => getNode(d.source).x || 0)
        .attr('y1', d => getNode(d.source).y || 0)
        .attr('x2', d => getNode(d.target).x || 0)
        .attr('y2', d => getNode(d.target).y || 0)

      node.attr('transform', d => `translate(${d.x || 0},${d.y || 0})`)
    })

    // Background click to deselect
    svg.on('click', () => setSelectedNode(null))

    // Cleanup function
    return () => {
      simulation.stop()
    }
  }, [observations, similarityThreshold, width, height])

  useEffect(() => {
    const cleanup = initializeGraph()
    return cleanup
  }, [initializeGraph])

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-700 border-t-cyan-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-96 text-red-400">
        <div className="text-4xl mb-4">⚠️</div>
        <div className="text-sm">{error}</div>
        <div className="text-xs text-gray-500 mt-2">Is the API running on localhost:3001?</div>
        <button 
          onClick={refetch}
          className="mt-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded text-sm transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  if (observations.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-96 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-4">📡</div>
          <div className="text-sm">No observations found</div>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Graph SVG */}
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="bg-gray-900/50 rounded-lg border border-gray-800"
      />

      {/* Legend */}
      <div className="absolute top-4 left-4 bg-gray-900/90 backdrop-blur rounded-lg p-3 border border-gray-800">
        <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Agents</div>
        {Object.entries(AGENT_CONFIG).map(([key, config]) => (
          <div key={key} className="flex items-center gap-2 mb-1 last:mb-0">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: config.color }}
            />
            <span className="text-xs text-gray-300">{config.name}</span>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="absolute top-4 right-4 bg-gray-900/90 backdrop-blur rounded-lg p-3 border border-gray-800">
        <div className="text-xs text-gray-400 mb-1 uppercase tracking-wider">Constellation</div>
        <div className="text-sm text-gray-300">{observations.length} nodes</div>
        <div className="text-xs text-gray-500">Similarity ≥ {similarityThreshold}</div>
        {isSimulating && (
          <div className="text-xs text-cyan-500 mt-1 animate-pulse">Simulating...</div>
        )}
      </div>

      {/* Selected node details panel */}
      {selectedNode && (
        <div 
          className="absolute bottom-4 left-4 right-4 bg-gray-900/95 backdrop-blur rounded-lg p-4 border border-gray-700 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: getNodeColor(selectedNode.observation.agent) }}
                />
                <span className="text-sm font-medium text-white">
                  {selectedNode.observation.agent}
                </span>
                <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-800 rounded">
                  {selectedNode.observation.obs_type}
                </span>
                <span className="text-xs text-gray-500">
                  confidence: {Math.round((selectedNode.observation.confidence || 0) * 100)}%
                </span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                {selectedNode.observation.content}
              </p>
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                <span>Source: {selectedNode.observation.source}</span>
                <span>•</span>
                <span>{new Date(selectedNode.observation.created_at).toLocaleString()}</span>
              </div>
            </div>
            <button 
              onClick={() => setSelectedNode(null)}
              className="ml-4 text-gray-500 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Instructions */}
      {!selectedNode && (
        <div className="absolute bottom-4 left-4 text-xs text-gray-600">
          Click nodes for details • Scroll to zoom • Drag to pan
        </div>
      )}
    </div>
  )
}
